import axios from "axios";
import { useConfigStore } from "../stores/config";
import {
    useApplicationStore,
    ApplicationStatus,
    RequestStatus,
    ApplicationAction,
} from "@/stores/application";
import { PartialForm, partialQuote } from "../entities/quote";
import z from "zod";
import { useQuoteStore } from "@/stores/quotes";
import { captureException } from "@sentry/vue";
import { getSessionId } from "@/utils";
import { handleApiResponse } from "@/utils/api";

export const insertPartialFormRequest = async (data: PartialForm) => {
    const config = useConfigStore();
    const response = await axios.post(`${config.apiUrl}/v1/annuities/partial?sessionId=${getSessionId()}`, data, {
        validateStatus: () => true,
    });

    const successSchema = z.object({
        response: z.object({
            partialId: z.number(),
            token: z.string(),
        }),
    });

    type SuccessResponse = z.infer<typeof successSchema>;
    const result = handleApiResponse<SuccessResponse>(response.data, successSchema, "insertPartialFormRequest");
    return result.response;
};

export const getPartialFromRequest = async (formId: number, token: string) => {
    const config = useConfigStore();
    const response = await axios.get(
        `${config.apiUrl}/v1/annuities/partial/${formId}`,
        {
            headers: {
                Authentication: "Bearer " + token,
            },
            validateStatus: () => true,
        },
    );

    const successSchema = z.object({
        response: partialQuote,
    });

    type SuccessResponse = z.infer<typeof successSchema>;
    const result = handleApiResponse<SuccessResponse>(response.data, successSchema, "getPartialFromRequest");
    return result.response;
};

export const getLatestPartialFormRequest = async (email: string, token: string) => {
    const config = useConfigStore();
    const response = await axios.post(
        `${config.apiUrl}/v1/annuities/partial/latest`,
        {
            email,
        },
        {
            headers: {
                Authentication: "Bearer " + token,
            },
            validateStatus: () => true,
        },
    );

    const successSchema = z.object({
        response: partialQuote,
    });

    type SuccessResponse = z.infer<typeof successSchema>;
    const result = handleApiResponse<SuccessResponse>(response.data, successSchema, "getLatestPartialFormRequest");
    return result.response;
};

export const insertPensionWiseAppointmentChoice = async (
    appointmentChoice: string, quoteNumber: string, token: string) => {
    const config = useConfigStore();
    const response = await axios.post(
        `${config.apiUrl}/v1/annuities/quote/${quoteNumber}/confirm-pensionwise?sessionId=${getSessionId()}`,
        {
            appointmentChoice,
        },
        {
            headers: {
                Authentication: token,
            },
            validateStatus: () => true,
        },
    );

    const successSchema = z.object({
        response: z.object({
            message: z.string(),
        }),
    });

    type SuccessResponse = z.infer<typeof successSchema>;
    const result = handleApiResponse<SuccessResponse>(
        response.data,
        successSchema,
        "insertPensionWiseAppointmentChoice",
    );
    return result.response;
};

export const submitApplicationData = async (quoteNumber: string, token: string, premiumId: number) => {
    const applicationStore = useApplicationStore();
    try {
        const config = useConfigStore();
        applicationStore.applicationRequestStatus = RequestStatus.Pending;
        const labels : Array<string> = ["customer", "dependant"];

        const transformedDefenceQuestions = applicationStore.defenceQuestions
            .reduce((acc: { [key: string]: boolean }, question) => {
                acc[question.name] = question.value || false;
                return acc;
            }, {});
        const generateBankDetails = () => {
            return applicationStore.companySlug === "lg"
                ? {
                    bank: {
                        accountHolder: applicationStore.paymentInformation.accountHolder,
                        accountNumber: applicationStore.paymentInformation.accountNumber?.toString(),
                        rollNumber: applicationStore.paymentInformation.buildingSociety.toString(),
                        sortCode: applicationStore.paymentInformation.sortCode,
                        name: applicationStore.paymentInformation.bankName,
                        branch: applicationStore.paymentInformation.bankBranchName,
                        address: applicationStore.paymentInformation.address,
                    },
                }
                : {};
        };
        const applicationData = {
            ...applicationStore.applicantDetails.reduce((acc, value, index) => {
                return {
                    ...acc,
                    [labels[index]]: {
                        forename: value.forename,
                        surname: value.surname,
                        phone: value.phone,
                        dob: value.dob,
                        email: index === 0 ? value.email : undefined,
                        address: value.address,
                        doctor: {
                            name: value.doctorsDetails.name,
                            address: {
                                line1: value.doctorsDetails.line1,
                                line2: value.doctorsDetails.line2,
                                city: value.doctorsDetails.city,
                                county: value.doctorsDetails.county,
                                postcode: value.doctorsDetails.postcode,
                            },
                            phone: value.doctorsDetails.phone,
                        },
                        nationalInsurance: value.nationalInsuranceNo.toUpperCase(),
                        seeMedicalReport: value.seeTheMedicalReport,
                    },
                };
            }, {}),
            product: {
                applicantAllowanceProtection: applicationStore.applicantDetails[0].applicantAllowanceProtection,
                applicantLifetimeAllowanceTaxProtection:
                    applicationStore.applicantDetails[0].applicantLifetimeAllowanceTaxProtection,
                applicantRemainingIlsa: applicationStore.applicantDetails[0].applicantRemainingIlsa,
            },
            funds: applicationStore.pensionFunds.map((fund) => {
                return {
                    type: fund.type,
                    provider: {
                        name: fund.provider,
                        address: {
                            line1: fund.address.line1,
                            line2: fund.address.line2,
                            city: fund.address.city,
                            county: fund.address.county,
                            postcode: fund.address.postcode,
                        },
                    },
                    schemeName: fund.pensionSchemeName,
                    planNumber: fund.planNumber,
                    fundValue: fund.totalFund,
                    fullValue: fund.fullValue,
                    fullValueReason: fund.fullValueReason,
                    fullTaxFreeAmount: fund.maxTaxFreeCash,
                    taxFreeAmount: fund.taxFreeCashAmount,
                    subjectTo: {
                        pensionSharing: fund.pensionSharingOrder,
                        bankruptcy: fund.bankruptcyOrder,
                        other: fund.otherCourtOrders,
                        earmarking: fund.earmarkingAttachmentOrder,
                    },
                };
            }),
            ...generateBankDetails(),
            defenceQuestions: transformedDefenceQuestions,
        };
        const response = await axios.post(
            `${config.apiUrl}/v1/annuities/quote/${quoteNumber}/premium/${premiumId}/create-application`,
            {
                ...applicationData,
            },
            {
                headers: {
                    Authentication: token,
                },
                validateStatus: () => true,
            },
        );

        const result = z.object({
            response: z.object({
                applicationId: z.number(),
            }),
            errors: z.array(z.object({ message: z.string() })).optional(),
        }).parse(response.data).response;

        applicationStore.applicationRequestStatus = RequestStatus.Succeeded;

        return result;
    } catch (err) {
        applicationStore.applicationRequestStatus = RequestStatus.Failed;
        throw err;
    }
};

export const pollApplication =
    async (quoteNumber: string, applicationId: number) : Promise<ApplicationStatus> => {
        const applicationStore = useApplicationStore();
        try {
            applicationStore.isPolling = true;
            const quoteStore = useQuoteStore();
            applicationStore.applicationStatus = ApplicationStatus.GeneratingPack;
            const config = useConfigStore();
            const quoteToken = quoteStore.latestTokenByQuoteNumber(quoteNumber);
            const timeout = config.pollTimeout;
            const finish = Date.now() + timeout;
            while (Date.now() < finish) {
                const maybeResponse = await axios.get(
                    // eslint-disable-next-line max-len
                    `${config.apiUrl}/v1/annuities/quote/${quoteNumber}/application/${applicationId}/request-pdf-pack?sessionId=${getSessionId()}`,
                    {
                        headers: {
                            Authentication: quoteToken.token,
                        },
                        validateStatus: () => true,
                    },
                );

                const response = z.object({
                    response: z.object({
                        status: z.nativeEnum(ApplicationStatus).optional(),
                    }),
                    errors: z.array(z.object({ message: z.string() })).optional(),
                }).parse(maybeResponse.data);

                if ((response.errors && response.errors.length > 0) || response.response.status === undefined) {
                    applicationStore.applicationStatus = ApplicationStatus.ErrorGeneratingPack;
                    applicationStore.isPolling = false;
                    throw new Error("Unable to retrieve application");
                }

                applicationStore.applicationStatus = response.response.status;
                if (response.response.status !== ApplicationStatus.GeneratingPack) {
                    applicationStore.isPolling = false;
                    return response.response.status;
                }
                await new Promise(resolve => setTimeout(resolve, config.pollFrequency));
            }
            throw new Error(`Unable to load quote: ${timeout}ms timeout exceeded`);
        } catch (err) {
            applicationStore.applicationStatus = ApplicationStatus.ErrorGeneratingPack;
            throw err;
        }
    };

export const submitPackOnline = async (quoteNumber: string, applicationId: number): Promise<ApplicationStatus> => {
    try {
        const quoteStore = useQuoteStore();
        const config = useConfigStore();
        const quoteToken = quoteStore.latestTokenByQuoteNumber(quoteNumber);

        const maybeResponse = await axios.get(
            `${config.apiUrl}/v1/annuities/quote/${quoteNumber}/application/${applicationId}/submit-pack-online`, {
                headers: {
                    Authentication: quoteToken.token,
                },
                validateStatus: () => true,
            });

        const response = z.object({
            response: z.object({
                status: z.nativeEnum(ApplicationStatus),
            }),
            errors: z.array(z.object({ message: z.string() })).optional(),
        }).parse(maybeResponse.data);

        return response.response.status;
    } catch (err) {
        captureException(err);
        return ApplicationStatus.ErrorSubmission;
    }
};

export const sendSubmitEmail = async (quoteNumber: string) => {
    try {
        const config = useConfigStore();
        const quoteStore = useQuoteStore();
        const applicationStore = useApplicationStore();
        const quoteToken = quoteStore.latestTokenByQuoteNumber(quoteNumber);
        const maybeResponse = await axios.get(
            // eslint-disable-next-line max-len
            `${config.apiUrl}/v1/annuities/quote/${quoteNumber}/application/${applicationStore.applicationId}/request-download-pack`,
            {
                headers: {
                    Authentication: quoteToken.token,
                },
            },
        );
        const response = z.object({
            response: z.object({
                status: z.nativeEnum(ApplicationAction),
            }),
            errors: z.array(z.object({ message: z.string() })).optional(),
        }).parse(maybeResponse.data);
        return response.response.status;
    } catch (err) {
        captureException(err);
        return ApplicationAction.ErrorRecieving;
    }
};

export const sendPostPackEmail = async (quoteNumber: string) => {
    const config = useConfigStore();
    const quoteStore = useQuoteStore();
    const applicationStore = useApplicationStore();
    const quoteToken = quoteStore.latestTokenByQuoteNumber(quoteNumber);
    try {
        const maybeResponse = await axios.get(
            // eslint-disable-next-line max-len
            `${config.apiUrl}/v1/annuities/quote/${quoteNumber}/application/${applicationStore.applicationId}/request-paper-pack`,
            {
                headers: {
                    Authentication: quoteToken.token,
                },
            },
        );
        const response = z.object({
            response: z.object({
                status: z.nativeEnum(ApplicationAction),
            }),
            errors: z.array(z.object({ message: z.string() })).optional(),
        }).parse(maybeResponse.data);
        return response.response.status;
    } catch (err) {
        captureException(err);
        return ApplicationAction.ErrorRecieving;
    }
};
